<template>
    <div class="ProgramServiceAssignmentEditor">
        <div v-if="CheckPermission(ViewPermissionRequirement)">
            <b-container>
                <b-row style="overflow-x:auto;">
                    <b-col cols="11">
                        <vuetable :fields="Fields"
                                  :data="FieldData"
                                  pagination-path=""
                                  :api-mode="false"
                                  class="vue-datatable"
                                  row-class="bold-first-row">
                        </vuetable>
                    </b-col>
                    <b-col cols="1">
                        <div v-if="EditMode && CheckPermission(EditPermission)" class="vert-icons">
                            <b-row v-if="TableState == 'NoRecords'">
                                <font-awesome-icon :icon="['fa', 'plus']" v-on:click="ShowAddRecord()" class="pointer olive-green-text" />
                            </b-row>
                            <b-row v-if="TableState == 'OneOpen'">
                                <font-awesome-icon :icon="['fas', 'exchange-alt']" v-on:click="ShowTransferRecord()" class="pointer olive-green-text" />
                            </b-row>
                            <b-row v-if="TableState == 'OneOpen'">
                                <font-awesome-icon :icon="['fas', 'trash-alt']" v-on:click="ShowDeleteRecord()" class="pointer red" />
                            </b-row>
                        </div>
                    </b-col>
                </b-row>
            </b-container>

            <!--ADD NEW RECORD MODAL-->
            <b-modal ref="add-modal" title="Add New Service" hide-footer>
                <b-container>
                    <b-form @submit="AddRecord">
                        <b-row>
                            <b-col>
                                <div class="form-group">
                                    <label>Service Name:</label>
                                    <select class="form-control" v-model="NewRecord.ProgramServiceID" required>
                                        <option v-for="option in NewRecordOptions" v-bind:value="option.Id">
                                            {{ option.Name }}
                                        </option>
                                    </select>
                                </div>
                                <div v-if="NewRecordHasCustomRate" class="form-group">
                                    <label>{{ CustomRateLabel }}:</label>
                                    <input type="number" step="0.01" v-model="NewRecord.CustomRate" class="form-control" required />
                                </div>
                                <div v-if="NewRecordHasRateSupplement" class="form-group">
                                    <label>{{ RateSupplementLabel }}:</label>
                                    <input type="number" step="0.01" v-model="NewRecord.RateSupplement" class="form-control" required />
                                </div>

                            </b-col>
                        </b-row>
                        <b-row class="modal-btn-row">
                            <div class="modal-btn-row-div">
                                <b-button size="md" variant="secondary" @click="CancelAddRecord()">
                                    CANCEL
                                </b-button>
                                <b-button size="md" variant="success" type="submit">
                                    SAVE
                                </b-button>
                            </div>
                        </b-row>
                    </b-form>
                </b-container>
            </b-modal>

            <!--TRANSFER RECORD MODAL-->
            <b-modal ref="transfer-modal" title="Modify Service" hide-footer>
                <b-container>
                    <b-form @submit="TransferRecord">
                        <b-row>
                            <b-col>
                                <div class="form-group">
                                    <label>New Service Name:</label>
                                    <select class="form-control" v-model="NewRecord.ProgramServiceID" required>
                                        <option v-for="option in NewRecordOptions" v-bind:value="option.Id">
                                            {{ option.Name }}
                                        </option>
                                    </select>
                                </div>
                                <div class="form-group">
                                    <label>Effective Date:</label>
                                    <flat-pickr v-model="NewRecord.EffectiveDate" class="form-control" ref="showfocus" required :config="DateConfig"></flat-pickr>
                                </div>
                                <div v-if="NewRecordHasCustomRate" class="form-group">
                                    <label>{{ CustomRateLabel }}:</label>
                                    <input type="number" step="0.01" v-model="NewRecord.CustomRate" class="form-control" required />
                                </div>
                                <div v-if="NewRecordHasRateSupplement" class="form-group">
                                    <label>{{ RateSupplementLabel }}:</label>
                                    <input type="number" step="0.01" v-model="NewRecord.RateSupplement" class="form-control" required />
                                </div>
                                <div v-if="RequirePreviousEndDate" class="form-group">
                                    <label>Previous Record End Date:</label>
                                    <flat-pickr v-model="NewRecord.PreviousEndDate" class="form-control" required :config="DateConfig"></flat-pickr>
                                </div>

                            </b-col>
                        </b-row>
                        <b-row class="modal-btn-row">
                            <div class="modal-btn-row-div">
                                <b-button size="md" variant="secondary" @click="CancelTransferRecord()">
                                    CANCEL
                                </b-button>
                                <b-button size="md" variant="success" type="submit">
                                    SAVE
                                </b-button>
                            </div>
                        </b-row>
                    </b-form>
                </b-container>
            </b-modal>

            <!--DELETE CURRENT RECORD MODAL-->
            <b-modal ref="delete-modal" title="Delete Service" hide-footer>
                <b-container>
                    <b-form @submit="DeleteRecord">
                        <b-row>
                            <b-col>
                                <p>You are about to delete the current service.  If there was a prior service, it will re-open and become the active service.</p>
                            </b-col>
                        </b-row>
                        <b-row class="modal-btn-row">
                            <div class="modal-btn-row-div">
                                <b-button size="md" variant="secondary" @click="CancelDeleteRecord()">
                                    CANCEL
                                </b-button>
                                <b-button size="md" variant="danger" type="submit">
                                    DELETE
                                </b-button>
                            </div>
                        </b-row>
                    </b-form>
                </b-container>
            </b-modal>
        </div>
        <div v-else>
            PRIVATE
        </div>
    </div>

</template>

<script>
    //import icons
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faPlus } from '@fortawesome/free-solid-svg-icons';
    library.add(faPlus);
    import { faExchangeAlt } from '@fortawesome/free-solid-svg-icons';
    library.add(faExchangeAlt);
    import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
    library.add(faTrashAlt);

    //import components
    import Vuetable from 'vuetable-2/src/components/Vuetable';
    import flatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';

    export default {
        props: [
            "EditPermission",
            "ViewPermissionRequirement",
            "EditMode",
            "SubProgramId",
            "RateSupplementLabel",
            "CustomRateLabel",
            "ChartId",
            "FieldData",
            "RequirePreviousEndDate",

        ],
        data: function () {
            return {
                NewRecord: {},
                NewRecordOptions: {},
                Fields: [
                    {
                        name: 'Label',
                        title: 'SERVICE'
                    },
                    {
                        name: 'StartDate',
                        title: 'STARTED',
                        callback: 'formatDate|MM/DD/YY'
                    },
                    {
                        name: 'EndDate',
                        title: 'ENDED',
                        callback: 'formatDate|MM/DD/YY|N/A'
                    }
                ],
                DateConfig: {
                    dateFormat: 'Y-m-d',
                    allowInput: true,
                    altInput: true,
                    altFormat: 'm/d/Y',
                },
            };
        },
        methods: {
            //ADD RECORD
            ShowAddRecord() {
                this.NewRecord = {};
                this.$MasterAPI.ExecuteRequest("GET", 'Core/ProgramServiceRate/GetProgramServicesForSubProgram', { Id: this.SubProgramId }, this.ShowAddRecordSuccess, () => { }, false);
                
            },
            ShowAddRecordSuccess(response) {
                 this.$set(this, "NewRecordOptions", response.data.data);
                 this.$refs['add-modal'].show();
            },
            AddRecord(e) {
                e.preventDefault();
                this.NewRecord.ClientSubProgramMembershipId = this.ChartId;
                this.$MasterAPI.ExecuteRequest("POST", "Core/ProgramServiceRate/AssignProgramServiceToClientSubProgramMembership", this.NewRecord, this.AddRecordSuccess, () => { }, true);
            },
             AddRecordSuccess(response) {
                this.$refs['add-modal'].hide();
                //Perform Global Updates
                this.$GlobalUpdate.ExecuteGlobalUpdate(this.ChartId);
            },
            CancelAddRecord() {
                this.$refs['add-modal'].hide();
            },
            //TRANSFER RECORD
            ShowTransferRecord() {
                this.NewRecord = {}
                this.$MasterAPI.ExecuteRequest("GET", 'Core/ProgramServiceRate/GetProgramServicesForSubProgram', { Id: this.SubProgramId }, this.ShowTransferRecordSuccess, () => { }, false);
            },
            ShowTransferRecordSuccess(response) {
                this.$set(this, "NewRecordOptions", response.data.data);
                this.$refs['transfer-modal'].show();
            },
            TransferRecord(e) {
                e.preventDefault();
                this.NewRecord.ClientSubProgramMembershipId = this.ChartId;
                this.$MasterAPI.ExecuteRequest("POST", "Core/ProgramServiceRate/TransferProgramServiceToClientSubProgramMembership", this.NewRecord, this.TransferRecordSuccess, () => { }, true);
            },
            TransferRecordSuccess(response) {
                this.$refs['transfer-modal'].hide();
                //Perform Global Updates
                this.$GlobalUpdate.ExecuteGlobalUpdate(this.ChartId);
            },
            CancelTransferRecord() {
                this.$refs['transfer-modal'].hide();
            },
            //DELETE RECORD
            ShowDeleteRecord() {
                this.$refs['delete-modal'].show();
            },
            DeleteRecord(e) {
                e.preventDefault();
                this.$MasterAPI.ExecuteRequest("POST", "Core/ProgramServiceRate/DeleteProgramServiceToClientSubProgramMembership", { Id: this.ChartId }, this.DeleteRecordSuccess, () => { }, true);
            },
            DeleteRecordSuccess(response) {
                this.$refs['delete-modal'].hide();
                //Perform Global Updates
                this.$GlobalUpdate.ExecuteGlobalUpdate(this.ChartId);
            },
            CancelDeleteRecord() {
                this.$refs['delete-modal'].hide();
            },

            CheckPermission(permissionName) {
                return this.$store.getters["User.PrivateProfile.CheckPermission"](permissionName);
            },
            formatDate (value, fmt = 'D MMM YYYY', defaultValue='') {
              return (value == null)
                ? defaultValue
                : moment(value, 'YYYY-MM-DD').format(fmt)
            },
        },
        components: {
            Vuetable,
            flatPickr
        },
        watch: {
            'NewRecord.EffectiveDate': function (newVal) {
                if (this.RequirePreviousEndDate && newVal) {
                    var date = moment(newVal, 'YYYY-MM-DD');
                    if (date.isValid()) {
                        this.$set(this.NewRecord, 'PreviousEndDate', date.subtract(1, 'days').format('YYYY-MM-DD'));
                    }
                }
            }
        },
        computed: {
            TableState: function () {
                if (this.FieldData.length == 0) {
                    return "NoRecords";
                } else if (this.FieldData.filter(datalist => datalist.EndDate == null).length == 0) {
                    return "AllClosed";
                } else {
                    return "OneOpen";
                }
            },
            NewRecordHasRateSupplement: function () {
                let vm = this;
                if (vm.NewRecord.ProgramServiceID) {
                    if (vm.NewRecordOptions.filter(x => x.Id == vm.NewRecord.ProgramServiceID)[0].CanHaveSupplementalAddition == true) {
                        return true;
                    }
                    else {
                        return false;
                    }
                } else {
                    return false;
                }
            },
            NewRecordHasCustomRate: function () {
                let vm = this;
                if (vm.NewRecord.ProgramServiceID) {
                    if (vm.NewRecordOptions.filter(x => x.Id == vm.NewRecord.ProgramServiceID)[0].IsHasCustomRate == true) {
                        return true;
                    }
                    else {
                        return false;
                    }
                } else {
                    return false;
                }
            }
        }
    }
</script>

<style scoped>
    .vert-icons div {
        margin-top:10px;
    }
    /deep/ tbody tr:first-child {
        font-weight: bold;
    }
    table {
        table-layout:auto;
    }
    .vue-datatable {
        width: 100%;
        display: inline-table;
        overflow-x: auto;
    }
</style>